import { CreateStore, createStore } from '@packages/stores';
import {
  AddSpecifiedItemToBoostArgument,
  ApiState,
  Boost,
  BoostArgument,
  CalendarData,
  PaymentOption,
  PreviouslySelectedCover,
  PrimaryTraveller,
  RemoveSpecifiedItemFromBoostArgument,
  ScreeningResultRequest,
  ScreeningResultResponse,
  SecondaryTraveller,
  TRIP_STATUS,
  ToggleBoostStatusArgument,
  Trip,
  TripDestination,
  UpdateBoostArgument,
  UpdateTripStatusArgument,
} from '@packages/types';
import { isBoostCruiseCover, isBoostRequired } from '@packages/utils';

import { useCheckoutStore } from '@store/checkout';
import { TOptionalFields } from '@types';

import * as quoteApi from '../quote/quote.api';
import * as tripApi from './trip.api';
import * as tripUtils from './trip.util';

export interface TripState {
  trip: Trip;
  error: Error | undefined;
  pending: boolean;
  boostApiState: Record<string, ApiState>;
  discountCodeApiState: ApiState;
  isCreatingOrUpdatingTrip: boolean;
  disabledBoostCardIds: Array<string>;
  addedSelectedCover: PreviouslySelectedCover | null;
  shouldShowCruiseModal: boolean;
}

interface SyncTripOption {
  quiet?: boolean;
}

export interface TripActions {
  setTrip: (trip?: Trip) => void;
  getTrip: () => Promise<Trip | undefined>;
  resetTripState: () => void;
  setPending: (pending: boolean) => void;
  setError: (error: Error | undefined) => void;
  toggleTripDestination: (destination: TripDestination) => void;
  setTripDestinations: (destinations: TripDestination[]) => void;
  setStartDate: (startDate: string) => void;
  setEndDate: (endDate: string) => void;
  updateBoost: (updateBoostArgument: UpdateBoostArgument) => Promise<Boost | undefined>;
  applyDiscountCode: (
    discountCode: string,
    email?: string,
    ignoreAnalytics?: boolean,
  ) => Promise<Trip | undefined>;
  removeDiscountCode: () => Promise<Trip | undefined>;
  setDisabledBoostCardIds: (listOfIds: string[]) => void;
  setTotalTripCost: (tripCost: number) => void;
  setAddCancellationBoost: (addCancellationBoost: boolean) => void;
  setTogglingBoostInTrip: (toggleBoostArgument: ToggleBoostStatusArgument) => void;
  removeBoost: (removeBoostArgument: BoostArgument) => Promise<void>;
  addSpecifiedItem: (
    addSpecifiedItemToBoostArgument: AddSpecifiedItemToBoostArgument,
  ) => Promise<Boost | void>;
  removeSpecifiedItem: (
    removeSpecifiedItemsFromBoostArgument: RemoveSpecifiedItemFromBoostArgument,
  ) => Promise<void>;
  toggleOffMutuallyExclusiveBoosts: (boostArgument: BoostArgument) => Promise<void>;
  addBoost: (addBoostArgument: UpdateBoostArgument) => Promise<Boost | undefined>;
  saveScreeningResult: (
    data: TOptionalFields<
      ScreeningResultRequest,
      | 'firstName'
      | 'lastName'
      | 'travellerId'
      | 'result'
      | 'boostId'
      | 'primaryTravellerId'
      | 'country'
      | 'isCompleted'
    >,
  ) => Promise<ScreeningResultResponse | undefined>;
  setPrimaryTraveller: (primaryTraveller: PrimaryTraveller) => void;
  setSecondaryTravellers: (secondaryTravellers: SecondaryTraveller[]) => void;
  syncTrip: (option?: SyncTripOption) => Promise<Trip | undefined>;
  validateTripDuration: ({ durationInDays }: CalendarData) => boolean;
  processFreeOfChargePromotion: () => Promise<void>;
  purchaseUsTrip: (paymentOption: PaymentOption) => Promise<void>;
  updateTripPolicyStatus: (argument: UpdateTripStatusArgument) => void;
  setAddedSelectedCover: (selectedCover: PreviouslySelectedCover | null) => void;
  validateTripStartDate: (startDate: string) => boolean;
  setIsCreatingOrUpdatingTrip: (isCreatingOrUpdatingTrip: boolean) => void;
  setShouldShowCruiseModal: (shouldShowCruiseModal: boolean) => void;
  setBoostApiState: (boostId: string, apiState: ApiState) => void;
  setDiscountCodeApiState: (apiState: ApiState) => void;
}

export type TripStore = TripState & TripActions;

export const initialState: TripState = {
  pending: false,
  disabledBoostCardIds: [],
  error: undefined,
  boostApiState: {},
  discountCodeApiState: 'idle',
  addedSelectedCover: null,
  isCreatingOrUpdatingTrip: false,
  shouldShowCruiseModal: false,
  trip: {
    sortKey: null,
    primaryTravellerId: null,
    primaryTraveller: null,
    secondaryTravellers: [],
    state: TRIP_STATUS.NO_BOOST,
    startDate: '',
    endDate: '',
    destinations: [],
    boosts: [],
    price: 0,
    pdsLink: null,
    presignedCoiLink: null,
    hasAddedBoosts: false,
    pendingUpdates: null,
    isTripStarted: false,
    policyStatus: 'NORMAL',
    productId: '',
  },
};

export const tripStore: CreateStore<TripStore> = (set, get) => ({
  ...initialState,
  toggleTripDestination: destination => tripUtils.toggleTripDestination(set, get, destination),
  setTripDestinations: destinations => tripUtils.setTripDestinations(set, destinations),
  setTrip: trip => tripUtils.setTrip(set, get, trip),
  setIsCreatingOrUpdatingTrip: isCreatingOrUpdatingTrip => {
    set(state => {
      state.isCreatingOrUpdatingTrip = isCreatingOrUpdatingTrip;
    });
  },
  resetTripState: () => set(initialState, false),
  saveScreeningResult: async data => await tripApi.saveScreeningResult(get, data),
  setStartDate: startDate => {
    set(state => {
      state.trip.startDate = startDate;
    });
  },
  setEndDate: endDate => {
    set(state => {
      state.trip.endDate = endDate;
    });
  },

  setTotalTripCost: tripCost => {
    set(state => {
      state.trip.totalTripCost = tripCost;
    });
  },
  getTrip: async () => await tripApi.fetchTrip(get),
  setPending: pending => {
    set(state => {
      state.pending = pending;
    });
  },
  setError: error => {
    set(state => {
      state.error = error;
    });
  },
  updateBoost: async updateBoostArgument => {
    useCheckoutStore.getState().setIsLegalConsentChecked(undefined);
    const isPaymentOpen = useCheckoutStore.getState().openSection === 'payment';
    if (isPaymentOpen) {
      useCheckoutStore.getState().setOpenSection('legalStuff');
    }
    return tripApi.updateBoostInTrip(get, updateBoostArgument);
  },
  applyDiscountCode: async (discountCode, email, ignoreAnalytics) => {
    return await tripApi.applyDiscountCodeInTrip(get, discountCode, email, ignoreAnalytics);
  },
  removeDiscountCode: async () => await tripApi.removeDiscountCodeFromTrip(get),
  setDisabledBoostCardIds: listOfIds =>
    set(state => {
      state.disabledBoostCardIds = listOfIds;
    }),
  setTogglingBoostInTrip: toggleBoostArgument =>
    tripUtils.toggleBoostStatusInTrip(set, toggleBoostArgument),
  removeBoost: async removeBoostArgument => {
    await tripUtils.removeBoostInTrip(get, removeBoostArgument);
  },
  addSpecifiedItem: async addSpecifiedItemToBoostArgument =>
    await tripUtils.addSpecifiedItemToTrip(get, addSpecifiedItemToBoostArgument),
  removeSpecifiedItem: async removeSpecifiedItemFromBoostArgument => {
    await tripUtils.removeSpecifiedItemFromTrip(get, removeSpecifiedItemFromBoostArgument);
  },
  toggleOffMutuallyExclusiveBoosts: async boostArgument => {
    await tripUtils.toggleOffMutuallyExclusiveBoostsInTrip(get, boostArgument);
  },
  addBoost: async addBoostArgument => {
    return await tripUtils.addBoostToTrip(get, addBoostArgument);
  },
  setPrimaryTraveller: primaryTraveller => {
    set(state => {
      state.trip.primaryTraveller = primaryTraveller;
    });
  },
  setSecondaryTravellers: secondaryTravellers =>
    set(state => {
      state.trip.secondaryTravellers = secondaryTravellers;
    }),
  syncTrip: async ({ quiet = false } = {}) => {
    const {
      trip: { sortKey },
    } = get();
    if (!quiet) {
      useCheckoutStore.getState().setOpenSection('travellerDetails');
      useCheckoutStore.getState().setIsLegalConsentChecked(undefined);
    }
    const trip = sortKey ? await quoteApi.updateQuote(get, quiet) : await quoteApi.createQuote(get);
    const cruiseBoost = trip?.boosts?.find(isBoostCruiseCover);
    const isCruiseRequired = isBoostRequired(cruiseBoost);
    const isCruiseAdded = !!cruiseBoost?.isAdded;
    if (isCruiseRequired && !isCruiseAdded) {
      get().setShouldShowCruiseModal(true);
    }

    return trip;
  },
  validateTripDuration: ({ durationInDays }) => tripUtils.validateTripDuration({ durationInDays }),
  validateTripStartDate: startDate => tripUtils.validateTripStartDate(startDate),
  processFreeOfChargePromotion: async () => await tripApi.processFreeOfChargePromotionInTrip(get),
  purchaseUsTrip: async paymentOption => await tripApi.purchaseUsTrip(get, paymentOption),
  updateTripPolicyStatus: ({ status, policyStatus, policyNumber }) => {
    set(state => {
      if (status) {
        state.trip.state = status;
      }
      if (policyStatus) {
        state.trip.policyStatus = policyStatus;
      }
      if (policyNumber) {
        state.trip.policy = { ...state.trip.policy, policyNumber };
      }
    });
  },

  setAddedSelectedCover: selectedCover => {
    set(state => {
      if (selectedCover === null) {
        state.addedSelectedCover = null;
        return;
      }

      state.addedSelectedCover = { ...state.addedSelectedCover, ...selectedCover };
    });
  },
  setShouldShowCruiseModal: shouldShowCruiseModal => {
    set(state => {
      state.shouldShowCruiseModal = shouldShowCruiseModal;
    });
  },
  setAddCancellationBoost: value => {
    set(state => {
      state.trip.additionalProperties = {
        ...state.trip.additionalProperties,
        addCancellationBoost: value,
      };
    });
  },
  setBoostApiState: (boostId, apiState) => {
    set(state => {
      state.boostApiState[boostId] = apiState;
    });
  },
  setDiscountCodeApiState: apiState => {
    set(state => {
      state.discountCodeApiState = apiState;
    });
  },
});

export const useTripStore = createStore(tripStore, {
  name: 'useTripStore',
});
